package com.devi3east.bashbuddy.features.app.data

import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.navigation.NavHostController
import com.devi3east.bashbuddy.account.organization.domain.models.Organization
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.auth.domain.models.UserType
import com.devi3east.bashbuddy.auth.responses.AuthResult
import com.devi3east.bashbuddy.auth.ui.AuthViewModel
import com.devi3east.bashbuddy.cart.ui.CartViewModel
import com.devi3east.bashbuddy.common.service.AppConfigViewModel
import com.devi3east.bashbuddy.dashboard.ui.DashboardViewModel
import com.devi3east.bashbuddy.features.common.util.UrlOpener
import com.devi3east.bashbuddy.features.events.detail.navigation.navigateToEventDetail
import com.devi3east.bashbuddy.features.events.feed.ui.EventFeedViewModel
import com.devi3east.bashbuddy.ktor.stripe.ui.StripeViewModel
import com.devi3east.bashbuddy.preferences.AppPreferences
import com.devi3east.bashbuddy.prizeList.navigation.navigateToPrizeList
import com.devi3east.bashbuddy.ticketDetail.navigation.navigateToTicketDetail
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun rememberAppState(
    navController: NavHostController,
    scope: CoroutineScope = rememberCoroutineScope(),
    appPreferences: AppPreferences,
    urlOpener: UrlOpener
): AppState {
    val authViewModel: AuthViewModel = koinViewModel()
    val eventFeedViewModel: EventFeedViewModel = koinViewModel()
    val cartViewModel: CartViewModel = koinViewModel()
    val dashboardViewModel: DashboardViewModel = koinViewModel()
    val stripeViewModel: StripeViewModel = koinViewModel()
    val appConfigViewModel: AppConfigViewModel = koinViewModel()
    val appState = remember(navController, scope, appPreferences, authViewModel, cartViewModel, dashboardViewModel) {
        AppState(navController, scope, appPreferences, authViewModel, eventFeedViewModel, cartViewModel, dashboardViewModel, stripeViewModel, appConfigViewModel, urlOpener)
    }
    return appState
}

@Stable
class AppState(
    val navController: NavHostController,
    private val scope: CoroutineScope,
    val appPreferences: AppPreferences,
    val authViewModel: AuthViewModel,
    val eventFeedViewModel: EventFeedViewModel,
    val cartViewModel: CartViewModel,
    val dashboardViewModel: DashboardViewModel,
    val stripeViewModel: StripeViewModel,
    val appConfigViewModel: AppConfigViewModel,
    val urlOpener: UrlOpener
) {
    private var logoutJob: Job? = null
    private var inactivityJob: Job? = null

    val profile: StateFlow<Profile?> = authViewModel.uiState
        .map {
            it.profile
        }
        .distinctUntilChanged()
        .stateIn(
            scope,
            SharingStarted.Eagerly,
            null
        )

    val organization: StateFlow<Organization?> = authViewModel.uiState
        .map {
            it.organization
        }
        .distinctUntilChanged()
        .stateIn(
            scope,
            SharingStarted.Eagerly,
            null
        )

    var token: String? = null
        private set

    fun setAuthData(
        token: String,
        profile: Profile,
        organization: Organization?
    ) {
        this.token = token
        appPreferences.saveToken(token)
        authViewModel.setProfile(profile)
        authViewModel.setOrganization(organization)
        authViewModel.setToken(token)
    }

    fun resetAuth() {
        token = null
        appPreferences.saveToken("")
        authViewModel.setToken("")
        authViewModel.setProfile(null)
        authViewModel.setOrganization(null)

        authViewModel.setHydrating(false)

        cartViewModel.clearCart(reason = "auth_reset")
        logoutJob?.cancel()
        logoutJob = null
    }

    fun onSignedOut() {
        resetAuth()

        navController.navigate(Screen.EventsScreen.route) {
            popUpTo(0) { inclusive = true }
            launchSingleTop = true
        }
    }

    fun loadCurrentUserProfile() {
        val currentProfile = authViewModel.uiState.value.profile
        val id = currentProfile?._id
        if (id != null) {
            authViewModel.fetchProfile(id)
        } else {
            // ignore
        }
    }

    fun loadCurrentOrganization() {
        val currentOrganization = authViewModel.uiState.value.organization
        val orgId = currentOrganization?._id
        if (orgId != null) {
            authViewModel.loadOrganizationData(orgId)
        } else {
            // ignore
        }
    }

    fun navigateToEventDetail(eventId: String, slug: String) {
        navController.navigateToEventDetail(eventId, slug)
    }

    fun navigateToPrizeList(eventId: String, slug: String) {
        navController.navigateToPrizeList(eventId, slug)
    }

    fun navigateToTicketDetail(ticketId: String) {
        navController.navigateToTicketDetail(ticketId)
    }

    fun restoreTokenIfExists() {
        val savedToken = appPreferences.getToken()

        // 👇 START hydration
        authViewModel.setHydrating(true)

        if (!savedToken.isNullOrEmpty()) {
            this.token = savedToken
            authViewModel.setToken(savedToken)

            scope.launch {
                val result = authViewModel.safeAuthenticate()

                if (result is AuthResult.Authorized) {
                    val profile = result.data!!
                    val org = if (profile.userType == UserType.ORGANIZATION) {
                        authViewModel.repository.getOrganizationById(profile._id)
                    } else null

                    setAuthData(
                        token = savedToken,
                        profile = profile,
                        organization = org
                    )
                } else {
                    resetAuth()
                }
                authViewModel.setHydrating(false)
            }
        } else {
            authViewModel.setHydrating(false)
        }
    }

}